/**
 * promo-cart-sync.js
 * ---------------------------------------------------------------------------
 * Unified auto-add-to-cart reconciler for Evidens de Beauté.
 * Replaces EG Auto Add to Cart. Handles three offer types in one pass:
 *
 *   1. Base samples   — tiered by paid cart value, always active
 *   2. Monthly offer  — threshold GWP, always active (date-gated via config)
 *   3. Partner promo  — discount-code-gated, multi-tier GWP + BOGO
 *
 * All free lines carry `_promo` (offer type) and `_promo_label` (badge text)
 * line-item properties. The companion Discount Function discounts any line
 * with `_promo` to 100% off. The theme's line-item.liquid renders the label.
 *
 * Config is read from window.__PROMO_CONFIG__ which is built from metaobjects
 * + inline data by snippets/promo-cart-sync.liquid.
 */
(function () {
  'use strict';

  const CONFIG = window.__PROMO_CONFIG__;
  if (!CONFIG) {
    console.warn('[promo-sync] __PROMO_CONFIG__ missing; aborting');
    return;
  }

  const PROP_KEY = CONFIG.propertyKey || '_promo';
  const LABEL_KEY = '_promo_label';
  const DEBUG = !!CONFIG.debug;
  const SELF_HEADER = 'X-Promo-Sync';

  const log = (...args) => { if (DEBUG) console.log('[promo-sync]', ...args); };

  let syncing = false;
  let pendingSync = false;
  let debounceTimer = null;
  let rateLimitRetries = 0;
  const MAX_RATE_LIMIT_RETRIES = 3;

  // -------------------------------------------------------------------------
  // Cart helpers
  // -------------------------------------------------------------------------

  async function getCart() {
    const res = await nativeFetch('/cart.js');
    return res.json();
  }

  function isPromoLine(line) {
    return line && line.properties && line.properties[PROP_KEY];
  }

  function hasDiscountCode(cart, code) {
    if (!code) return false;
    const target = code.toUpperCase();
    return (cart.discount_codes || []).some(
      d => d.code && d.code.toUpperCase() === target
    );
  }

  function indexPromoLines(cart) {
    const current = new Map();
    for (const line of cart.items) {
      if (!isPromoLine(line)) continue;
      const tag = line.properties[PROP_KEY];
      const key = `${line.variant_id}::${tag}`;
      current.set(key, line);
    }
    return current;
  }

  // -------------------------------------------------------------------------
  // Expected-lines computation — one function per offer type
  // -------------------------------------------------------------------------

  function addExpected(map, variantId, quantity, promoTag, label) {
    const key = `${variantId}::${promoTag}`;
    map.set(key, {
      variantId,
      quantity,
      properties: { [PROP_KEY]: promoTag, [LABEL_KEY]: label }
    });
  }

  /** 1. Base samples — always active, one sample tier per cart value. */
  function addSampleTierLines(expected, subtotalBase) {
    const tiers = (CONFIG.sampleTiers || [])
      .filter(t => t.items && t.items.length)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    let matchedTier = null;
    for (const tier of tiers) {
      if (subtotalBase >= tier.min && subtotalBase < (tier.max || 999999)) {
        matchedTier = tier;
        break;
      }
    }

    if (matchedTier) {
      log('sample tier matched:', matchedTier.id, { min: matchedTier.min, max: matchedTier.max });
      for (const item of matchedTier.items) {
        addExpected(expected, item.variantId, item.quantity || 1, matchedTier.id, matchedTier.label || 'FREE SAMPLES');
      }
    }
  }

  /** 2. Monthly offer — always active, but excluded when a partner promo is active
   *  or when the customer has dismissed the current offer. */
  function addMonthlyOfferLines(expected, subtotalBase, cart) {
    const offer = CONFIG.monthlyOffer;
    if (!offer || !offer.items || !offer.items.length) return;

    // Exclude monthly offer when any partner promo discount code is active
    for (const promo of CONFIG.partnerPromos || []) {
      if (hasDiscountCode(cart, promo.discountCode)) {
        log('monthly offer skipped: partner promo active', promo.id);
        return;
      }
    }

    // Exclude monthly offer when the customer has dismissed it for this offer.
    // The attribute stores the offer's metaobject handle (not a boolean) so that
    // a stale flag from a previous offer automatically becomes inert when a new
    // offer rotates in.
    const dismissedHandle = (cart.attributes && cart.attributes._monthly_gift_dismissed) || '';
    if (offer.handle && dismissedHandle === offer.handle) {
      log('monthly offer skipped: dismissed by customer', offer.handle);
      return;
    }

    if (subtotalBase >= offer.min) {
      log('monthly offer matched:', { min: offer.min });
      for (const item of offer.items) {
        addExpected(expected, item.variantId, item.quantity || 1, offer.id || 'monthly', offer.label || 'MONTHLY OFFER');
      }
    }
  }

  /** 3. Partner promos — gated by discount code (market gate done in Liquid). */
  function addPartnerPromoLines(expected, cart, subtotalBase, paidLines) {
    const promos = CONFIG.partnerPromos || [];

    for (const promo of promos) {
      if (!hasDiscountCode(cart, promo.discountCode)) continue;

      log('partner promo eligible:', promo.id);

      // BOGO — identify eligible paid lines for the free duplicate.
      const bogoSet = new Set((promo.bogoVariantIds || []).map(Number));

      // Tier GWP — count all paid non-promo products, including paid BOGO items.
      // Generated free BOGO lines are excluded earlier because they carry `_promo`.
      // Convert to base currency using the same rate as the main subtotal.
      const tierSubtotalCents = paidLines
        .reduce((s, l) => s + l.final_line_price, 0);
      const currencyRate = window.Shopify?.currency?.rate || 1;
      const tierSubtotalBase = (tierSubtotalCents / 100) / currencyRate;

      log('partner tier subtotal (paid lines, base currency):', tierSubtotalBase.toFixed(2));

      const tiers = (promo.tiers || []).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      let matchedTier = null;
      for (const tier of tiers) {
        if (tierSubtotalBase >= tier.min && tierSubtotalBase < (tier.max || 999999)) {
          matchedTier = tier;
          break;
        }
      }

      if (matchedTier) {
        log('partner tier matched:', matchedTier.id, { min: matchedTier.min, max: matchedTier.max });
        for (const item of matchedTier.items) {
          addExpected(expected, item.variantId, item.quantity, matchedTier.id, promo.label || 'PARTNER GIFT');
        }
      }

      // BOGO — add free duplicate for each BOGO-eligible paid line
      for (const line of paidLines) {
        if (bogoSet.has(Number(line.variant_id))) {
          addExpected(expected, line.variant_id, line.quantity, 'bogo_' + promo.id, promo.label || 'BUY ONE GET ONE');
        }
      }
    }
  }

  /** Master: compute ALL expected promo lines across all offer types. */
  function computeExpectedLines(cart) {
    const paidLines = cart.items.filter(l => !isPromoLine(l) && l.final_line_price > 0);
    const paidSubtotalCents = paidLines.reduce((s, l) => s + l.final_line_price, 0);
    const subtotalPresentment = paidSubtotalCents / 100;

    // Convert presentment currency back to shop base currency (EUR) for threshold
    // comparison. Shopify.currency.rate is the multiplier from base → presentment.
    // e.g., for JPY with rate ~160: ¥15,000 / 160 = €93.75
    const currencyRate = window.Shopify?.currency?.rate || 1;
    const subtotalBase = subtotalPresentment / currencyRate;

    log('paid subtotal:', { presentment: subtotalPresentment, currency: cart.currency, rate: currencyRate, base: subtotalBase.toFixed(2) });

    const expected = new Map();

    // 1. Base samples — use presentment subtotal when market thresholds are active
    // (thresholds are already in the visitor's currency), fall back to base for EUR defaults
    const sampleSubtotal = CONFIG.sampleUsePresentment ? subtotalPresentment : subtotalBase;
    addSampleTierLines(expected, sampleSubtotal);

    // 2. Monthly offer (skipped when partner promo is active)
    addMonthlyOfferLines(expected, subtotalBase, cart);

    // 3. Partner promo (gated)
    addPartnerPromoLines(expected, cart, subtotalBase, paidLines);

    return expected;
  }

  // -------------------------------------------------------------------------
  // Cart mutation helpers
  // -------------------------------------------------------------------------

  // Native fetch captured in <head> before any app could patch it.
  // Bypasses interceptors from Polar, Growave, shop_events_listener, etc.
  const nativeFetch = window.__nativeFetch || window.fetch.bind(window);
  const origFetch = window.fetch; // Patched version — used for mutation detection only

  async function cartUpdate(updates) {
    const res = await nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (res.status === 429) { log('cart/update rate limited'); return 'rate_limited'; }
    if (!res.ok) { log('cart/update failed', res.status); return 'error'; }
    return 'ok';
  }

  async function cartAdd(items) {
    const res = await nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (res.status === 429) { log('cart/add rate limited'); return 'rate_limited'; }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      log('cart/add failed', res.status, body.description || body.message || '');
      return 'error';
    }
    return 'ok';
  }

  // -------------------------------------------------------------------------
  // Reconcile
  // -------------------------------------------------------------------------

  async function reconcile() {
    if (syncing) { pendingSync = true; return; }
    syncing = true;
    try {
      const cart = await getCart();
      log('reconcile: cart has', cart.items.length, 'lines');

      const expected = computeExpectedLines(cart);
      const current = indexPromoLines(cart);

      // Compute diff
      const toRemoveKeys = [];
      const qtyUpdates = {};
      for (const [k, line] of current) {
        if (!expected.has(k)) toRemoveKeys.push(line.key);
      }
      for (const [k, exp] of expected) {
        const cur = current.get(k);
        if (cur && cur.quantity !== exp.quantity) qtyUpdates[cur.key] = exp.quantity;
      }
      const toAdd = [];
      for (const [k, exp] of expected) {
        if (!current.has(k)) {
          toAdd.push({
            id: exp.variantId,
            quantity: exp.quantity,
            properties: exp.properties
          });
        }
      }

      const hasChanges = toRemoveKeys.length || Object.keys(qtyUpdates).length || toAdd.length;
      if (!hasChanges) {
        log('cart already in sync');
        rateLimitRetries = 0;
        return;
      }

      log('diff', {
        remove: toRemoveKeys.length,
        qtyUpdate: Object.keys(qtyUpdates).length,
        add: toAdd.length
      });

      // Phase 1: removals + qty updates
      let result = 'ok';
      if (toRemoveKeys.length || Object.keys(qtyUpdates).length) {
        const updates = {};
        for (const k of toRemoveKeys) updates[k] = 0;
        Object.assign(updates, qtyUpdates);
        log('updates', updates);
        result = await cartUpdate(updates);
      }

      // Phase 2: additions (skip if phase 1 failed)
      if (result === 'ok' && toAdd.length) {
        log('adding', toAdd);
        result = await cartAdd(toAdd);
      }

      // On rate limit: exponential backoff, hard cap
      if (result === 'rate_limited') {
        rateLimitRetries++;
        if (rateLimitRetries > MAX_RATE_LIMIT_RETRIES) {
          log('max rate limit retries reached, stopping until next user action');
          return;
        }
        const delay = Math.min(5000 * rateLimitRetries, 15000); // 5s, 10s, 15s
        log('rate limited, retry', rateLimitRetries, 'of', MAX_RATE_LIMIT_RETRIES, 'in', delay + 'ms');
        // Use a dedicated timeout, NOT triggerSync (which can be re-triggered by other events)
        setTimeout(() => { syncing = false; reconcile(); }, delay);
        return; // Don't release syncing lock — the timeout will
      }

      // On real error: stop, don't retry
      if (result === 'error') {
        log('cart mutation failed, will retry on next user action');
        return;
      }

      // Success — reset rate limit counter
      rateLimitRetries = 0;

      // Phase 3: verification pass (only on success)
      const verifyCart = await getCart();
      const verifyExpected = computeExpectedLines(verifyCart);
      const verifyCurrent = indexPromoLines(verifyCart);
      let needsFix = false;
      for (const [k] of verifyExpected) {
        if (!verifyCurrent.has(k)) { needsFix = true; break; }
      }
      if (!needsFix) {
        for (const [k] of verifyCurrent) {
          if (!verifyExpected.has(k)) { needsFix = true; break; }
        }
      }
      if (needsFix) {
        log('post-reconcile drift, running correction');
        const fixUpdates = {};
        for (const [k, line] of verifyCurrent) {
          if (!verifyExpected.has(k)) fixUpdates[line.key] = 0;
        }
        if (Object.keys(fixUpdates).length) {
          if (await cartUpdate(fixUpdates) !== 'ok') return;
        }
        const fixAdds = [];
        for (const [k, exp] of verifyExpected) {
          if (!verifyCurrent.has(k)) {
            fixAdds.push({ id: exp.variantId, quantity: exp.quantity, properties: exp.properties });
          }
        }
        if (fixAdds.length) {
          if (await cartAdd(fixAdds) !== 'ok') return;
        }
      }

      rateLimitRetries = 0;
      notifyThemeCartChanged();
    } catch (err) {
      console.error('[promo-sync] reconcile error:', err);
    } finally {
      syncing = false;
      if (pendingSync) {
        pendingSync = false;
        setTimeout(reconcile, 800);
      }
    }
  }

  function triggerSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 800);
  }

  function notifyThemeCartChanged() {
    document.dispatchEvent(new CustomEvent('promo:cart-synced', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (window.location.pathname.endsWith('/cart')) {
      log('reloading cart page');
      window.location.reload();
    }
  }

  // -------------------------------------------------------------------------
  // Mutation detection
  // -------------------------------------------------------------------------

  const CART_MUTATION_RE = /\/cart\/(add|update|change|clear)(\.js)?/;

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isSelf = init && init.headers && (
      (init.headers[SELF_HEADER] === '1') ||
      (typeof init.headers.get === 'function' && init.headers.get(SELF_HEADER) === '1')
    );
    const promise = origFetch.apply(this, arguments);
    if (!isSelf && CART_MUTATION_RE.test(url)) {
      promise.then(() => triggerSync()).catch(() => {});
    }
    return promise;
  };

  const origXhrOpen = XMLHttpRequest.prototype.open;
  const origXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__promoUrl = url;
    return origXhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__promoUrl && CART_MUTATION_RE.test(this.__promoUrl)) {
      this.addEventListener('load', () => triggerSync());
    }
    return origXhrSend.apply(this, arguments);
  };

  // Initial sync — short delay to let the page settle. Our cart calls now use
  // nativeFetch (bypasses app interceptors) so rate limit contention is minimal.
  function initialSync() {
    setTimeout(triggerSync, 800);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialSync);
  } else {
    initialSync();
  }

  // -------------------------------------------------------------------------
  // Monthly gift dismiss / claim handlers (cart page)
  // -------------------------------------------------------------------------

  /**
   * Dismiss: mark the current monthly offer as declined for this cart and
   * remove the gift line in a single /cart/update.js call. Then reload the
   * cart page so the server re-renders with the banner visible.
   *
   * Uses nativeFetch (bypasses the patched window.fetch interceptor), so
   * the interceptor's triggerSync() does NOT fire from this call. That's
   * fine — we reload immediately after, and initialSync runs reconcile
   * on the reloaded page, which keeps sample tiers and partner promos
   * consistent with any cart changes that happened during dismiss.
   */
  async function handleDismissMonthlyGift(btn) {
    const offer = CONFIG.monthlyOffer;
    if (!offer || !offer.handle) {
      console.error('[promo-sync] dismiss clicked but no active offer handle');
      return;
    }
    const lineKey = btn.getAttribute('data-line-key');
    if (!lineKey) {
      console.error('[promo-sync] dismiss clicked but no line key on button');
      return;
    }

    btn.disabled = true;
    try {
      const res = await nativeFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { _monthly_gift_dismissed: offer.handle },
          updates: { [lineKey]: 0 }
        })
      });
      if (!res.ok) throw new Error('cart update failed: ' + res.status);
      log('monthly gift dismissed; reloading cart page');
      window.location.reload();
    } catch (err) {
      console.error('[promo-sync] dismiss failed:', err);
      btn.disabled = false;
      // Surface minimal feedback — the button is still in DOM, let the user retry
      btn.setAttribute('data-error', '1');
    }
  }

  /**
   * Claim: clear the dismiss flag, run reconcile to re-add the gift line,
   * then reload. Order matters — we clear the flag before calling reconcile
   * so the reconciler's dismiss check passes and the line gets added.
   */
  async function handleClaimMonthlyGift(btn) {
    // No CONFIG.monthlyOffer guard needed: claim only clears the attribute
    // and asks the reconciler to re-evaluate. If the offer is gone the
    // reconciler correctly does nothing. If the offer changed, the cleared
    // attribute lets the new offer auto-add on its own terms.
    btn.disabled = true;
    try {
      const clearRes = await nativeFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [SELF_HEADER]: '1' },
        body: JSON.stringify({ attributes: { _monthly_gift_dismissed: '' } })
      });
      if (!clearRes.ok) throw new Error('clear attribute failed: ' + clearRes.status);

      // Force an immediate reconcile (bypass debounce) so the gift line is
      // present by the time we reload.
      await reconcile();

      log('monthly gift claimed; reloading cart page');
      window.location.reload();
    } catch (err) {
      console.error('[promo-sync] claim failed:', err);
      btn.disabled = false;
      btn.setAttribute('data-error', '1');
    }
  }

  // Delegated listener — banner and button are both rendered by Liquid and
  // may not exist on page load if the customer doesn't qualify / hasn't
  // dismissed. Delegation keeps this cheap.
  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-action]');
    if (!target) return;
    const action = target.getAttribute('data-action');
    if (action === 'dismiss-monthly-gift') {
      event.preventDefault();
      handleDismissMonthlyGift(target);
    } else if (action === 'claim-monthly-gift') {
      event.preventDefault();
      handleClaimMonthlyGift(target);
    }
  });

  window.__promoSync = { reconcile, getCart, triggerSync, CONFIG };
  log('initialized — unified reconciler');
})();
